import { decodeImage, detectImageKind, canvasToBlob, hasTransparency } from "./decode";

/**
 * Determines the correct output MIME type and file extension for an
 * uploaded image, for tools that re-encode and download a result in the
 * same format the user uploaded.
 *
 * `file.type` is the natural first choice, but it's a real, documented
 * File API quirk that it can come back empty or wrong in some
 * browser/OS combinations -- and a naive `file.type || "image/png"`
 * fallback silently produces a PNG-encoded file with a filename that
 * still claims the original extension, which is exactly the bug this
 * was built to fix (reported as files downloading as .jfif, and a
 * simple flip/rotate ballooning a 26KB JPEG into a 384KB PNG). Falling
 * back to the file's own extension first is far more often correct than
 * assuming PNG.
 */
export function getImageOutputFormat(file: File): { mimeType: string; extension: string } {
  const recognizedTypes = new Set(["image/jpeg", "image/png", "image/webp", "image/gif", "image/bmp"]);
  if (file.type && recognizedTypes.has(file.type)) {
    const extension = file.type === "image/jpeg" ? "jpg" : file.type.split("/")[1];
    return { mimeType: file.type, extension };
  }

  if (file.type === "image/heic" || file.type === "image/heif" || /\.(heic|heif)$/i.test(file.name)) {
    return { mimeType: "image/jpeg", extension: "jpg" };
  }

  const nameMatch = file.name.match(/\.([a-zA-Z0-9]+)$/);
  const ext = nameMatch ? nameMatch[1].toLowerCase() : "";
  const extToMime: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    jfif: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    gif: "image/gif",
    bmp: "image/bmp",
  };
  if (ext in extToMime) {
    return { mimeType: extToMime[ext], extension: ext === "jpeg" || ext === "jfif" ? "jpg" : ext };
  }

  // Genuinely couldn't determine the format from either signal -- PNG is
  // a safe, lossless default rather than guessing wrong.
  return { mimeType: "image/png", extension: "png" };
}

/**
 * Loads a File into an HTMLImageElement, resolving once it's ready to draw. Formats a browser cannot
 * read by itself (HEIC, TIFF, AVIF on older browsers) are decoded first via decodeImage, so every
 * tool that calls this accepts the same files.
 */
export async function loadImageElement(file: File): Promise<HTMLImageElement> {
  const kind = detectImageKind(file);
  if (kind === "native") {
    try {
      return await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    } catch { /* fall through to the full decoder for a clear message */ }
  }
  const { canvas } = await decodeImage(file);
  const blob = await canvasToBlob(canvas, "image/png");
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
}

const TARGET_MIN_QUALITY = 0.1;
const TARGET_MAX_QUALITY = 0.95;
const TARGET_QUALITY_STEPS = 8;
/** Aim a little under the limit: portals that count 1 KB as 1000 bytes would reject a file that is
 *  exactly 1024-byte-KB. 0.97 keeps us inside both readings for any limit above ~1 KB. */
export const TARGET_SAFETY = 0.97;

export interface ImageCompressionOutcome {
  blob: Blob;
  size: number;
  metTarget: boolean;
  targetBytes: number;
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
  /** MIME type and file extension of the result (JPEG for photos, WebP when transparency must survive). */
  mimeType: string;
  extension: string;
  /** True when the picture had to be made smaller (fewer pixels) to reach the target. */
  resized: boolean;
  /** True when the original was already under the target, so it is returned untouched. */
  alreadyUnder: boolean;
  hadTransparency: boolean;
  /**
   * Set when the original is handed back untouched and `alreadyUnder` is not the reason:
   * "vector" for an SVG (already a small vector file; drawing it as pixels would only make it bigger),
   * "not-smaller" when no setting produced a file smaller than the original.
   */
  keptOriginal?: "vector" | "not-smaller";
}

export interface CompressOptions {
  /** Never reduce width/height; only lower quality (the result may then miss the target). */
  keepDimensions?: boolean;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
  /** Longest the target search may run, in milliseconds. After that the best result so far is returned. */
  maxMs?: number;
}

/** An SVG is already text-sized vector art; Compress Image returns it unchanged. */
export function isSvgFile(file: { name: string; type: string }): boolean {
  return (file.type || "").toLowerCase() === "image/svg+xml" || /\.svgz?$/i.test(file.name);
}

function svgOutcome(file: File, targetBytes: number): ImageCompressionOutcome {
  return { blob: file, size: file.size, metTarget: true, targetBytes, width: 0, height: 0, originalWidth: 0, originalHeight: 0, mimeType: "image/svg+xml", extension: "svg", resized: false, alreadyUnder: false, hadTransparency: true, keptOriginal: "vector" };
}

const tick = () => new Promise<void>((r) => setTimeout(r, 0));

async function webpSupported(): Promise<boolean> {
  const c = document.createElement("canvas");
  c.width = c.height = 1;
  const b = await new Promise<Blob | null>((r) => c.toBlob(r, "image/webp", 0.5));
  return !!b && b.type === "image/webp";
}

/** Decide the output format: JPEG for opaque pictures; WebP (keeps transparency) when there is any. */
async function pickFormat(canvas: HTMLCanvasElement): Promise<{ mimeType: string; extension: string; transparent: boolean }> {
  const transparent = hasTransparency(canvas);
  if (transparent && (await webpSupported())) return { mimeType: "image/webp", extension: "webp", transparent };
  return { mimeType: "image/jpeg", extension: "jpg", transparent };
}

function drawScaled(src: HTMLCanvasElement, scale: number, opaque: boolean): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(src.width * scale));
  c.height = Math.max(1, Math.round(src.height * scale));
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  if (opaque) { ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height); }
  ctx.drawImage(src, 0, 0, c.width, c.height);
  return c;
}

/**
 * Compresses an image to fit under a target byte size. Quality is lowered first (binary search);
 * the picture is made smaller ONLY when even the lowest quality cannot reach the target, and only if
 * `keepDimensions` is off. The outcome says exactly what was done (size, pixels, format).
 */
export async function compressImageToTarget(file: File, targetBytes: number, opts: CompressOptions = {}): Promise<ImageCompressionOutcome> {
  if (isSvgFile(file)) return svgOutcome(file, targetBytes);
  const out = await compressImageToTargetRaw(file, targetBytes, opts);
  return keepIfNotSmaller(file, out);
}

/** Never offer a "compressed" file that is bigger than (or as big as) the original. */
function keepIfNotSmaller(file: File, out: ImageCompressionOutcome): ImageCompressionOutcome {
  if (out.alreadyUnder || out.keptOriginal || out.size < file.size) return out;
  return { ...out, blob: file, size: file.size, mimeType: file.type || out.mimeType, extension: (file.name.match(/\.([a-z0-9]+)$/i)?.[1] ?? out.extension).toLowerCase(), width: out.originalWidth, height: out.originalHeight, resized: false, keptOriginal: "not-smaller" };
}

async function compressImageToTargetRaw(file: File, targetBytes: number, opts: CompressOptions): Promise<ImageCompressionOutcome> {
  const started = Date.now();
  const maxMs = opts.maxMs ?? 20000;
  const { canvas: source } = await decodeImage(file);
  const fmt = await pickFormat(source);
  const goal = Math.max(1, Math.floor(targetBytes * TARGET_SAFETY));
  const base = { originalWidth: source.width, originalHeight: source.height, mimeType: fmt.mimeType, extension: fmt.extension, hadTransparency: fmt.transparent };

  // Already under the target: nothing to do, and never hand back something bigger.
  if (file.size <= targetBytes && detectImageKind(file) === "native") {
    return { blob: file, size: file.size, metTarget: true, targetBytes, width: source.width, height: source.height, resized: false, alreadyUnder: true, ...base, mimeType: file.type || base.mimeType, extension: (file.name.match(/\.([a-z0-9]+)$/i)?.[1] ?? base.extension).toLowerCase() };
  }

  const scales = opts.keepDimensions ? [1] : [1, 0.85, 0.7, 0.55, 0.4, 0.3, 0.2, 0.12];
  const opaque = fmt.mimeType === "image/jpeg";
  const totalSteps = scales.length * TARGET_QUALITY_STEPS;
  let done = 0;
  let fullSizeFloor: { blob: Blob; w: number; h: number } | null = null;

  for (const scale of scales) {
    if (Date.now() - started > maxMs) break; // time cap: settle for the smallest we have
    const canvas = drawScaled(source, scale, opaque);
    let lo = TARGET_MIN_QUALITY;
    let hi = TARGET_MAX_QUALITY;
    let best: Blob | null = null;
    for (let i = 0; i < TARGET_QUALITY_STEPS; i++) {
      if (opts.signal?.aborted) throw new DOMException("Cancelled", "AbortError");
      const q = (lo + hi) / 2;
      const blob = await canvasToBlob(canvas, fmt.mimeType, q);
      if (blob.size <= goal) { best = blob; lo = q; } else { hi = q; }
      opts.onProgress?.(Math.min(0.99, ++done / totalSteps));
      await tick();
    }
    if (best) return { blob: best, size: best.size, metTarget: true, targetBytes, width: canvas.width, height: canvas.height, resized: scale !== 1, alreadyUnder: false, ...base };
    const floor = await canvasToBlob(canvas, fmt.mimeType, TARGET_MIN_QUALITY);
    if (scale === 1) fullSizeFloor = { blob: floor, w: canvas.width, h: canvas.height };
  }
  // Not reachable even after shrinking: return the full-size, lowest-quality version rather than a
  // needlessly tiny picture that still misses the target.
  if (!fullSizeFloor) {
    const c = drawScaled(source, 1, opaque);
    fullSizeFloor = { blob: await canvasToBlob(c, fmt.mimeType, TARGET_MIN_QUALITY), w: c.width, h: c.height };
  }
  const s = fullSizeFloor;
  return { blob: s.blob, size: s.blob.size, metTarget: false, targetBytes, width: s.w, height: s.h, resized: false, alreadyUnder: false, ...base };
}

/** Manual-quality compression: same format rules, dimensions never change. */
export async function compressImageAtQuality(file: File, quality: number): Promise<ImageCompressionOutcome> {
  if (isSvgFile(file)) return svgOutcome(file, file.size);
  return keepIfNotSmaller(file, await compressImageAtQualityRaw(file, quality));
}

async function compressImageAtQualityRaw(file: File, quality: number): Promise<ImageCompressionOutcome> {
  const { canvas: source } = await decodeImage(file);
  const fmt = await pickFormat(source);
  const canvas = drawScaled(source, 1, fmt.mimeType === "image/jpeg");
  const blob = await canvasToBlob(canvas, fmt.mimeType, quality);
  return { blob, size: blob.size, metTarget: true, targetBytes: blob.size, width: canvas.width, height: canvas.height, originalWidth: source.width, originalHeight: source.height, mimeType: fmt.mimeType, extension: fmt.extension, resized: false, alreadyUnder: false, hadTransparency: fmt.transparent };
}

/** Replaces a filename's extension with the correct one for the given
 * output format, so the downloaded file's name always matches its real
 * encoded content. */
export function withCorrectExtension(filename: string, extension: string): string {
  return filename.replace(/\.[a-zA-Z0-9]+$/, "") + "." + extension;
}
